// src/pages/admin/ActivityLogs.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { logService, companyService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showErrorToast } from '../../utils/toastUtils';
import {
  Search, Download, RefreshCw, Activity, LogIn, AlertTriangle,
  ChevronUp, ChevronDown, CheckCircle2, Shield, Clock, Server, Building2
} from 'lucide-react';

const ActivityLogs = () => {
  const location = useLocation();

  // State for data
  const [logs, setLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({ totalLogs: 0, todayLogs: 0, loginLogs: 0, errorLogs: 0 });

  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Real-time debounce states
  const [searchInput, setSearchInput] = useState('');

  // Server-side filters
  const [filters, setFilters] = useState({
    search: '',
    companyId: '',
    actionType: '',
    startDate: '',
    endDate: '',
    userId: ''
  });

  const [pagination, setPagination] = useState({
    page: 1, limit: 20, total: 0, pages: 1
  });

  const actionTypes = [
    { value: '', label: 'Toutes les actions' },
    { value: 'USER_LOGIN', label: 'Connexion' },
    { value: 'USER_LOGOUT', label: 'Déconnexion' },
    { value: 'USER_CREATE', label: 'Création compte' },
    { value: 'USER_UPDATE', label: 'Modif. compte' },
    { value: 'USER_DELETE', label: 'Suppr. compte' },
    { value: 'COMPANY_CREATE', label: 'Création entreprise' },
    { value: 'COMPANY_UPDATE', label: 'Modif. entreprise' },
    { value: 'COMPANY_DELETE', label: 'Suppr. entreprise' },
    { value: 'ANALYSIS_CREATE', label: 'Nouvelle analyse' },
    { value: 'ANALYSIS_UPDATE', label: 'Modif. analyse' },
    { value: 'ANALYSIS_DELETE', label: 'Suppr. analyse' }
  ];

  // Initial setup for userId from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const uId = searchParams.get('userId');
    if (uId) {
      setFilters(p => ({ ...p, userId: uId }));
    }
  }, [location.search]);

  // Load Initial metadata
  useEffect(() => {
    loadMetadata();
  }, []);

  const loadMetadata = async () => {
    try {
      const compRes = await companyService.getCompanies({ limit: 1000 });
      if (compRes.success) {
        setCompanies([{ id: '', name: 'Toutes les entités' }, ...(compRes.data || [])]);
      }

      const statRes = await logService.getActivityStats();
      if (statRes.success && statRes.data) {
        const { totalLogs, actionTypeCounts } = statRes.data;
        const logins = actionTypeCounts.find(a => a.type === 'USER_LOGIN')?.count || 0;
        const errors = actionTypeCounts.find(a => a.type && a.type.includes('ERROR'))?.count || 0;

        const todayStr = new Date().toISOString().split('T')[0];
        const todayData = statRes.data.activityByDay?.find(day => day._id === todayStr);

        setStats({
          totalLogs: totalLogs || 0,
          todayLogs: todayData?.count || 0,
          loginLogs: logins,
          errorLogs: errors
        });
      }
    } catch (e) {
      console.warn("Échec du chargement des metadonnées", e);
    }
  };

  // Main fetch function
  const fetchLogs = useCallback(async (page = 1, fetchBackground = false) => {
    if (!fetchBackground) setIsLoading(true);
    setIsRefreshing(true);

    try {
      const params = { page, limit: pagination.limit, sort: '-timestamp' };

      // Apply populated filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) params[key] = filters[key];
      });

      const response = await logService.getLogs(params);

      if (response.success) {
        setLogs(response.data || []);
        if (response.pagination) {
          setPagination(prev => ({ ...prev, ...response.pagination }));
        }
      }
    } catch (error) {
      showErrorToast("Erreur lors du chargement des journaux globaux.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [pagination.limit, filters]);

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Refetch on filter change
  useEffect(() => {
    if (!isLoading) fetchLogs(1, true);
  }, [filters]);

  // Initial fetch
  useEffect(() => {
    fetchLogs(1);
  }, []); // eslint-disable-line

  const handleExportTxt = () => {
    if (logs.length === 0) return showErrorToast("Aucun log à exporter");

    let content = "===== EXPORT DES JOURNAUX D'ACTIVITÉ (SUPERADMIN) =====\n";
    content += `Généré le: ${new Date().toLocaleString('fr-FR')}\n`;
    content += `Filtres appliqués:\n`;
    content += `  - Recherche globale: ${filters.search || 'Aucune'}\n`;
    content += `  - Entreprise ID: ${filters.companyId || 'Toutes'}\n`;
    content += `  - Type d'action: ${filters.actionType || 'Toutes'}\n`;
    content += `  - Période: ${filters.startDate || '-'} à ${filters.endDate || '-'}\n`;
    content += "=========================================================\n\n";

    logs.forEach(log => {
      const details = typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details || '-');
      const date = new Date(log.timestamp).toLocaleString('fr-FR');
      content += `[${date}] Entité: ${log.companyName || 'Admin Central'} | Utilisateur: ${log.userName} | Action: ${log.actionType} | IP: ${log.ipAddress}\n`;
      if (details !== '{}' && details !== '-') {
        content += `    Détails: ${details}\n`;
      }
      content += '\n';
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Ebiosens_SuperAdmin_Logs_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getBadgeStyle = (action) => {
    if (!action) return 'bg-gray-100 text-gray-700 border-gray-200';
    if (action.includes('CREATE') || action.includes('APPROVE')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (action.includes('UPDATE')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (action.includes('DELETE') || action.includes('REJECT') || action.includes('ERROR')) return 'bg-rose-50 text-rose-700 border-rose-200';
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getActionIcon = (action) => {
    if (!action) return <Activity size={14} />;
    if (action.includes('CREATE')) return <CheckCircle2 size={14} className="text-emerald-500" />;
    if (action.includes('DELETE')) return <AlertTriangle size={14} className="text-rose-500" />;
    if (action.includes('LOGIN')) return <LogIn size={14} className="text-indigo-500" />;
    if (action.includes('ERROR')) return <AlertTriangle size={14} className="text-rose-600" />;
    return <Activity size={14} className="text-blue-500" />;
  };

  return (
    <div className="p-4 md:p-8 max-w-full mx-auto bg-gray-50/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <GlobeIcon className="w-8 h-8 text-blue-700" />
            Superviseur Central : Logs
          </h1>
          <p className="text-gray-500 mt-1.5 text-sm font-medium">
            Vue non filtrée de tous les événements de la plateforme EBIOSens Multi-Tenant
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExportTxt}
            disabled={logs.length === 0}
            className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 hover:text-blue-600 focus:ring-4 focus:ring-gray-100 transition-all font-medium text-sm shadow-sm disabled:opacity-50"
          >
            <Download size={18} />
            Exporter (TXT)
          </button>
          <button
            onClick={() => fetchLogs(1)}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-200 transition-all font-medium text-sm shadow-sm"
          >
            <RefreshCw size={18} className={isRefreshing && !isLoading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Volume Empreintes', value: stats.totalLogs, icon: Server, color: 'blue' },
          { label: 'Trafic du Jour', value: stats.todayLogs, icon: Activity, color: 'emerald' },
          { label: 'Accès & Sessions', value: stats.loginLogs, icon: LogIn, color: 'indigo' },
          { label: 'Anomalies', value: stats.errorLogs, icon: AlertTriangle, color: 'rose' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-600`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Advanced Filters */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Search */}
          <div className="col-span-1 border-r-0 md:border-r border-gray-100 md:col-span-3 md:pr-4">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Traque universelle</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="ID, IP, Utilisateur..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
          </div>

          <div className="col-span-1 md:col-span-9 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company Dropdown */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block flex items-center gap-1.5"><Building2 size={12} /> Entité (Tenant)</label>
              <select
                value={filters.companyId}
                onChange={(e) => setFilters(p => ({ ...p, companyId: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all appearance-none cursor-pointer truncate"
              >
                {companies.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
              </select>
            </div>

            {/* Action */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Transaction</label>
              <select
                value={filters.actionType}
                onChange={(e) => setFilters(p => ({ ...p, actionType: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all appearance-none cursor-pointer"
              >
                {actionTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Dates */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Dès le</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(p => ({ ...p, startDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Jusqu'au</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(p => ({ ...p, endDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Multi-tenant Log Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
          <LoadingSpinner />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-4 bg-gray-50 rounded-full mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Aucune trace dans tout le parc</h3>
          <p className="text-gray-500 mt-1 max-w-sm text-center text-sm">
            Aucun événement ne correspond à ces critères d'audit.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-40">Horodatage</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-48">Entité</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-64">Acteur</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Mouvement</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-16 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => {
                  const isExpanded = expandedRow === log.id;

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`hover:bg-slate-50 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                      >
                        <td className="py-3 px-6 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-xs text-gray-500 font-mono">
                            {new Date(log.timestamp).toLocaleString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', second: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="py-3 px-6">
                          <span className="text-sm font-semibold text-indigo-900 truncate block max-w-[160px]">
                            {log.companyName || 'Interne Core'}
                          </span>
                        </td>
                        <td className="py-3 px-6">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-900 block truncate max-w-[150px]">
                              {log.userName || 'Système Root'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-6">
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide border rounded-md flex items-center gap-1.5 w-max ${getBadgeStyle(log.actionType)}`}>
                              {getActionIcon(log.actionType)}
                              {log.actionType || 'INCONNU'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-6 text-center">
                          <button className="p-1 rounded bg-white border shadow-sm text-gray-500 transition-colors">
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </td>
                      </tr>

                      {/* Detailed Trace Viewer */}
                      {isExpanded && (
                        <tr className="bg-slate-900 border-t border-slate-800">
                          <td colSpan="5" className="p-0">
                            <div className="px-6 py-4 flex gap-4">
                              <div className="w-80 border-r border-slate-700 pr-4">
                                <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                                  <Shield size={14} className="text-emerald-400" /> Signature d'Exécution
                                </h4>
                                <div className="space-y-3">
                                  <div>
                                    <span className="text-[10px] text-slate-500 block uppercase">Origine (IP)</span>
                                    <span className="text-xs font-mono text-emerald-400">{log.ipAddress || 'Locale'}</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] text-slate-500 block uppercase">Log Trace ID</span>
                                    <span className="text-xs font-mono text-slate-300 break-all">{log.id}</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] text-slate-500 block uppercase">User ID</span>
                                    <span className="text-xs font-mono text-slate-300 break-all">{log.userId || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex-1 pl-2">
                                <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-2">
                                  <Server size={14} className="text-blue-400" /> Objet de Contexte (JSON Dump)
                                </h4>
                                <pre className="text-[11px] font-mono whitespace-pre-wrap text-blue-300 bg-slate-800/50 p-3 rounded border border-slate-700/50 max-h-40 overflow-y-auto">
                                  {log.details && Object.keys(log.details).length > 0
                                    ? JSON.stringify(log.details, null, 2)
                                    : '// Aucune payload envoyée au contrôleur pour cette transaction'}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 0 && (
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">
                Moteur affichant de {(pagination.page - 1) * pagination.limit + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur un univers de {pagination.total} enregistrements
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Précédent
                </button>
                <div className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold shadow-inner">
                  {pagination.page} / {pagination.pages}
                </div>
                <button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GlobeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21.128 12A10.01 10.01 0 0012 2a10.01 10.01 0 00-9.128 10M12 22a10.01 10.01 0 009.128-10M12 22a10.01 10.01 0 01-9.128-10" /></svg>;

export default ActivityLogs;